import axios from "axios";
import { apiClient, adminApiClient, getToken } from "./apiClient";

// Local backend API base URL
const LOCAL_API_BASE = process.env.REACT_APP_LOCAL_API_BASE || "http://localhost:5000/api";
const REMOTE_EVENTS_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.connectummah.com/api";
const REMOTE_ANNOUNCEMENTS_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.connectummah.com/api";

// Create axios instance for local backend
const localApiClient = axios.create({
  baseURL: LOCAL_API_BASE,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json"
  }
});

/**
 * Enhanced Prayer Times Service
 * Fetches prayer times from multiple sources with fallback
 */
// export const getPrayerTimesEnhanced = async (masjidId, token = null) => {
//   // try {
//   //   // Try local backend first (which proxies to ConnectUmmah)
//   //   const response = await localApiClient.get(`/prayer-times/${masjidId}`, {
//   //     params: { token }
//   //   });
//   //   return response.data;
//   // } catch (error) {
//   //   console.warn("Local API failed, trying direct ConnectUmmah API...", error.message);
    
//     // Fallback to direct API call
//     try {
//       const directResponse = await localApiClient.get(`/masjid/${masjidId}`, {
//         params: { t: Date.now() },
//         headers: {
//           "Cache-Control": "no-cache",
//           "Pragma": "no-cache",
//           "Expires": "0",
//         },
//       });
//       return directResponse.data;
//     } catch (directError) {
//       console.error("Both local and direct API calls failed:", directError);
//       throw new Error("Failed to fetch prayer times from all sources");
//     }
//   // }
// };

/**
 * Enhanced Events Service
 * Fetches events with processed image URLs
 */
export const getEventsEnhanced = async (masjidId) => {
  const BASE_IMAGE_URL = "https://cdn.connectummah.com/myuploadfiles/";
  const normalizeEvents = (payload) => {
    const events = Array.isArray(payload) ? payload : (payload?.data || []);

    return events.map((event) => {
      const firstImage = event?.images?.find?.((img) => img?.imageLink)?.imageLink;
      return {
        ...event,
        image: firstImage ? `${BASE_IMAGE_URL}${firstImage}` : event?.image || null,
        name: event.name || event.title || "Unnamed Event",
        description: event.description || "",
        startDate: event.startDate,
        endDate: event.endDate,
        location: event.location || ""
      };
    });
  };

  try {
    const response = await localApiClient.get(`/events/${masjidId}`);
    return normalizeEvents(response.data);
  } catch (error) {
    console.warn("Local events API failed, trying direct ConnectUmmah API...", error?.message || error);

    try {
      const directResponse = await axios.get(
        `${REMOTE_EVENTS_BASE.replace(/\/+$/, "")}/CommunityEvent/GetForMasjidId/${masjidId}`
      );
      return normalizeEvents(directResponse.data);
    } catch (directError) {
      console.error("Both local and direct API calls failed:", directError);
      throw new Error("Failed to fetch events from all sources");
    }
  }
};

/**
 * Enhanced Announcements Service
 */
export const getAnnouncementsEnhanced = async (masjidId) => {
  try {
    const response = await localApiClient.get(`/announcements/${masjidId}`);
    return response.data;
  } catch (error) {
    console.warn("Local announcements API failed, trying direct ConnectUmmah API...", error?.message || error);

    try {
      const directResponse = await axios.get(
        `${REMOTE_ANNOUNCEMENTS_BASE.replace(/\/+$/, "")}/Announcements/GetAnnouncementsForMasjid/${masjidId}`,
        {
          headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
        }
      );
      return directResponse.data;
    } catch (directError) {
      console.error("Both local and direct API calls failed:", directError);
      throw new Error("Failed to fetch announcements from all sources");
    }
  }
};

/**
 * Enhanced Configuration Service
 */
export const getConfigurationEnhanced = async (publicId = null) => {
  const PUBLIC_ID = publicId || process.env.REACT_APP_PUBLIC_ID || "VZ4D08";
  
  // try {
  //   // Try local backend first
  //   const response = await localApiClient.get(`/config/${PUBLIC_ID}`, {
  //     params: { token }
  //   });
  //   return response.data;
  // } catch (error) {
  //   console.warn("Local API failed, trying direct Admin API...", error.message);
    
    // Fallback to direct API call
  //   try {
  //     const directResponse = await adminApiClient.get(
  //       `/Display_Config/GetDisplay_ConfigForPublicId`,
  //       { params: { public_Id: PUBLIC_ID } }
  //     );
  //     return directResponse.data;
  //   } catch (directError) {
  //     console.error("Both local and direct API calls failed:", directError);
  //     throw new Error("Failed to fetch configuration from all sources");
  //   }
  // }
};

/**
 * Enhanced Hijri Date Service
 */
export const getHijriDateEnhanced = async (date = null, lat = null, lng = null) => {
  // Normalize to DD-MM-YYYY (AlAdhan expects dashes)
  const raw = date || new Date().toLocaleDateString('en-GB'); // e.g. 07/10/2025
  const dateStr = raw.includes('/') ? raw.split('/').join('-') : raw;
  
  // try {
  //   // Try local backend first
  //   const response = await localApiClient.get('/hijri-date', {
  //     params: { date: dateStr, lat, lng }
  //   });
  //   return response.data;
  // } catch (error) {
  //   console.warn("Local API failed, trying direct AlAdhan API...", error.message);
    
    // Fallback to direct API call
    try {
      let url = `https://api.aladhan.com/v1/gToH?date=${dateStr}`;
      if (lat && lng) {
        url += `&latitude=${lat}&longitude=${lng}`;
      }

      const directResponse = await axios.get(url);
      const hijri = directResponse.data?.data?.hijri;

      if (hijri && hijri.month?.en && hijri.day && hijri.year) {
        const HIJRI_MONTHS_UPPER = [
          "MUHARRAM","SAFAR","RABĪʿ AL-AWWAL","RABĪʿ AL-THĀNĪ","JUMĀDĀ AL-ŪLĀ","JUMĀDĀ AL-ĀKHIRAH","RAJAB","SHAʿBĀN","RAMADĀN","SHAWWĀL","DHŪ AL-QIʿDAH","DHŪ AL-ḤIJJAH"
        ];
        const monthIndex = Number(hijri.month?.number) - 1;
        const monthName = HIJRI_MONTHS_UPPER[monthIndex] || String(hijri.month?.en || "").toUpperCase();
        const formatted = `${Number(hijri.day)} ${monthName}  ${Number(hijri.year)}`;
        return {
          formatted,
          day: Number(hijri.day),
          month: { ...hijri.month, number: Number(hijri.month?.number) },
          year: Number(hijri.year),
          weekday: hijri.weekday
        };
      } else {
        throw new Error("Invalid Hijri data from API");
      }
    } catch (directError) {
      console.error("Both local and direct API calls failed:", directError);
      // Return empty formatted to avoid showing an alarming label; frontend will fetch client-side
      return { formatted: "" };
    }
  // }
};

/**
 * Prayer Times by Coordinates Service
 */
export const getPrayerTimesByCoords = async (lat, lng, method = 2) => {
  // try {
  //   // Try local backend first
  //   const response = await localApiClient.get('/prayer-times-coords', {
  //     params: { lat, lng, method }
  //   });
  //   return response.data;
  // } catch (error) {
  //   console.warn("Local API failed, trying direct AlAdhan API...", error.message);
    
    // Fallback to direct API call
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const directResponse = await axios.get(
        `https://api.aladhan.com/v1/timings/${today}?latitude=${lat}&longitude=${lng}&method=${method}`
      );
      return directResponse.data;
    } catch (directError) {
      console.error("Both local and direct API calls failed:", directError);
      throw new Error("Failed to fetch prayer times by coordinates from all sources");
    }
  // }
};

/**
 * Services/Programs Service
 */
// export const getServicesEnhanced = async () => {
//   // try {
//   //   // Try local backend first
//   //   const response = await localApiClient.get('/services');
//   //   return response.data;
//   // } catch (error) {
//   //   console.warn("Local API failed, returning static services...", error.message);
    
//     // Fallback to static data
//     return [
//       {
//         id: 1,
//         title: "5 DAILY PRAYERS 12121",
//         description: "At BICC, we hold five daily prayers to hold the most important obligation of our community.",
//         image: "/api/placeholder/300/200",
//         category: "worship"
//       },
//       {
//         id: 2,
//         title: "2 JUMU'AH PRAYERS",
//         description: "BICC holds 2 Jumu'ah prayers due to the ever-increasing size of our community.",
//         image: "/api/placeholder/300/200",
//         category: "worship"
//       },
//       {
//         id: 3,
//         title: "TARAWEEH IN RAMADAN",
//         description: "Taraweeh prayers are held in Ramadan and Quran is recited by the most qualified Qurra.",
//         image: "/api/placeholder/300/200",
//         category: "seasonal"
//       },
//       {
//         id: 4,
//         title: "SISTERS FRIDAY NIGHT",
//         description: "Every Friday night, the Centre is dedicated to sisters to provide them an environment to discuss and gain knowledge.",
//         image: "/api/placeholder/300/200",
//         category: "community"
//       },
//       {
//         id: 5,
//         title: "ARABIC COURSE",
//         description: "The Arabic Course Program provides adult males with an easy way to understand the Quran.",
//         image: "/api/placeholder/300/200",
//         category: "education"
//       },
//       {
//         id: 6,
//         title: "SPECIAL EVENTS",
//         description: "Special events for the youngest members to ensure they have the strongest Islamic foundation.",
//         image: "/api/placeholder/300/200",
//         category: "youth"
//       }
//     ];
//   // }
// };

/**
 * Health Check Service
 */
// export const checkApiHealth = async () => {
//   try {
//     const response = await localApiClient.get('/health');
//     return response.data;
//   } catch (error) {
//     console.error("Health check failed:", error.message);
//     return { status: "ERROR", message: error.message };
//   }
// };

/**
 * Comprehensive Data Fetcher
 * Fetches all necessary data for a masjid screen in one call
 */
export const getAllMasjidData = async (masjidId, publicId = null) => {
  try {
    const [
      // prayerTimes,
      events,
      announcements,
      configuration,
      hijriDate,
      // services
    ] = await Promise.allSettled([
      // getPrayerTimesEnhanced(masjidId),
      getEventsEnhanced(masjidId),
      getAnnouncementsEnhanced(masjidId),
      getConfigurationEnhanced(publicId),
      getHijriDateEnhanced(),
      // getServicesEnhanced()
    ]);

    return {
      // prayerTimes: prayerTimes.status === 'fulfilled' ? prayerTimes.value : null,
      events: events.status === 'fulfilled' ? events.value : [],
      announcements: announcements.status === 'fulfilled' ? announcements.value : [],
      configuration: configuration.status === 'fulfilled' ? configuration.value : null,
      hijriDate: hijriDate.status === 'fulfilled' ? hijriDate.value : { formatted: "" },
      // services: services.status === 'fulfilled' ? services.value : [],
      errors: {
        // prayerTimes: prayerTimes.status === 'rejected' ? prayerTimes.reason : null,
        events: events.status === 'rejected' ? events.reason : null,
        announcements: announcements.status === 'rejected' ? announcements.reason : null,
        configuration: configuration.status === 'rejected' ? configuration.reason : null,
        hijriDate: hijriDate.status === 'rejected' ? hijriDate.reason : null,
        // services: services.status === 'rejected' ? services.reason : null,
      }
    };
  } catch (error) {
    console.error("Error fetching all masjid data:", error);
    throw error;
  }
};

const config ={
  // getPrayerTimesEnhanced,
  getEventsEnhanced,
  getAnnouncementsEnhanced,
  getConfigurationEnhanced,
  getHijriDateEnhanced,
  getPrayerTimesByCoords,
  // getServicesEnhanced,
  // checkApiHealth,
  getAllMasjidData
};
export default config;
